/**
 * Time formatting utilities for displaying Daily Time Records
 */

import { manilaYMD } from './periodDate';

/**
 * Convert Firestore Timestamp to "hh:mm AM/PM" format
 * @param {Object} timestamp - Firestore Timestamp object
 * @returns {string|null} Formatted time string or null if no timestamp
 */
export const formatFirestoreTimestamp = (timestamp) => {
  if (!timestamp || !timestamp.toDate) return null;

  try {
    const date = timestamp.toDate();
    // Manila clock time, whatever the browser's zone (PRD_TimeRecordAndPayFixes C3).
    return date.toLocaleTimeString('en-US', {
      timeZone: 'Asia/Manila',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return null;
  }
};

/**
 * Order a single DAY's records chronologically.
 *
 * The stored `timeRecords` array is in ENTRY order — records are appended with
 * arrayUnion (timeRecordService.js:172), and an EDIT is arrayRemove +
 * arrayUnion, which moves the edited record to the end. So a split shift
 * entered afternoon-first, or an edited morning shift, would otherwise render
 * with the later shift above the earlier one on both the screen and the DTR
 * report. Shared by both so they cannot disagree (PRD_DtrReport C21).
 *
 * Sorts on `timeIn`, FALLING BACK TO `timeOut`. The fallback is load-bearing,
 * not defensive: the kiosk persists an orphaned clock-out as a real record with
 * `timeIn: null` and a valid `timeOut` (recordClockEvent.js:355-357). Keying
 * those at 0 would sort them above every real shift and — because both
 * renderers bind the date cell to the first row of the day — hand them the
 * date label too. Sorted on their clock-out they land where they happened.
 *
 * A record with neither time keys at 0 and leads the day, which is where an
 * unparseable row should be: visible, not silently last.
 *
 * NB `sortTimeRecords` below is NOT reused: its clock-out branch only fires for
 * records tagged `displayMode: 'time-out-only'` by filterRecordsByDate, which
 * neither caller here does, so it would reproduce exactly the bug above.
 *
 * @param {Array} records - one date's time records
 * @returns {Array} a NEW array, ordered chronologically
 */
export const sortRecordsByTimeIn = (records) => {
  const at = (r) => {
    if (r?.timeIn?.toDate) return r.timeIn.toDate().getTime();
    if (r?.timeOut?.toDate) return r.timeOut.toDate().getTime();
    return 0;
  };
  return [...(records || [])].sort((a, b) => at(a) - at(b));
};

/**
 * Get month key for caching
 * @param {Date} date - Date object
 * @returns {string} Month key in format "yyyy-mm"
 */
export const getMonthKey = (date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Is this date the first or last day of its month?
 *
 * Moved here from the TimeTracking component body so it can be unit-tested
 * (PRD_TimeTrackingRecordFetch C10) — it was an unexported closure.
 *
 * @param {Date} date - Date object
 * @returns {boolean} True on the 1st or the last day of the month
 */
export const isMonthBoundary = (date) => {
  const day = date.getDate();
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  return day === 1 || day === lastDay;
};

/**
 * Month key of the previous/next month relative to `date`.
 *
 * Built from (year, month ± 1, 1) — NOT `setMonth` on a copy of `date`. Setting the
 * month on the 31st overflows into the month after the one you asked for when the
 * target is shorter: Aug 31 + setMonth(8) is "Sep 31" => Oct 1, which returned
 * '2026-10' instead of '2026-09' (same for Jan/Mar/May/Oct 31). Day 1 is always
 * valid, and the Date constructor normalizes month 12 / -1 across the year boundary.
 * PRD_TimeTrackingRecordFetch C10 — this drives a Firestore query, so a wrong key
 * fetches the wrong month.
 *
 * @param {Date} date - Date object
 * @param {'previous'|'next'} direction - Which neighbour to return
 * @returns {string} Month key in format "yyyy-mm"
 */
export const getAdjacentMonth = (date, direction) => {
  const offset = direction === 'previous' ? -1 : 1;
  return getMonthKey(new Date(date.getFullYear(), date.getMonth() + offset, 1));
};

/**
 * Check if date strings match
 * @param {string} date1 - Date string in YYYY-MM-DD format
 * @param {string} date2 - Date string in YYYY-MM-DD format
 * @returns {boolean} True if dates match
 */
export const isSameDateString = (date1, date2) => date1 === date2;

/**
 * Get date from Firestore timestamp
 * @param {Object} timestamp - Firestore Timestamp object
 * @returns {string|null} Date in YYYY-MM-DD format or null
 */
const getDateFromTimestamp = (timestamp) => {
  if (!timestamp || !timestamp.toDate) return null;
  // The Manila date of the instant (C3) — compared against the record's Manila `date`.
  return manilaYMD(timestamp.toDate());
};

/**
 * Get time value for sorting records
 * @param {Object} record - Time record object
 * @param {string} displayMode - Display mode (time-in-only, time-out-only, both)
 * @returns {number} Timestamp value for sorting
 */
const getTimeValue = (record, displayMode) => {
  if (displayMode === 'time-out-only' && record.timeOut) {
    return record.timeOut.toDate ? record.timeOut.toDate().getTime() : 0;
  }
  if (record.timeIn) {
    return record.timeIn.toDate ? record.timeIn.toDate().getTime() : 0;
  }
  return 0;
};

/**
 * Sort time records by time in or time out based on display mode
 * @param {Array} records - Array of time record objects
 * @returns {Array} Sorted array of time records
 */
export const sortTimeRecords = (records) => {
  if (!records || !Array.isArray(records)) return [];

  return [...records].sort((a, b) => {
    // For time-out-only entries, sort by time out
    // For others, sort by time in
    const timeA = getTimeValue(a, a.displayMode);
    const timeB = getTimeValue(b, b.displayMode);
    return timeA - timeB;
  });
};

/**
 * Filter time records for a specific date
 * Records are shown on their clock-in date only (per PRD_AttendancePolicy cross-day shift rule)
 * Overnight shifts display all data with an isOvernight flag for +1 badge rendering
 * @param {Array} records - Array of all time records
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Array} Filtered array of time records
 */
export const filterRecordsByDate = (records, dateString) => {
  if (!records || !Array.isArray(records)) return [];

  const filtered = [];

  records.forEach(record => {
    const timeInDate = record.date; // Already stored as YYYY-MM-DD

    if (timeInDate === dateString) {
      const timeOutDate = record.timeOut ?
        getDateFromTimestamp(record.timeOut) : null;
      const isOvernight = timeOutDate && timeOutDate !== dateString;

      filtered.push({
        ...record,
        displayMode: 'both',
        isOvernight,
        timeOutDate: isOvernight && record.timeOut?.toDate ? record.timeOut.toDate() : null
      });
    }
  });

  // Sort by time in
  filtered.sort((a, b) => {
    const timeA = getTimeValue(a, 'both');
    const timeB = getTimeValue(b, 'both');
    return timeA - timeB;
  });

  return filtered;
};